import { useState } from 'react';
import clsx from 'clsx';
import Image from '@/components/Image';
import Container from '@/components/Container';
import MarketingSection from './MarketingSection';

function MarketingTestimonialImage({
  title,
  sourceUrl,
}) {
  return (
    <div className="flex-none w-14 h-14 bg-white rounded-full flex items-center justify-center">
      <Image alt={`Imagen de testminonio ${title}`} className="w-12 h-12 rounded-full bg-green-500" src={sourceUrl} />
    </div>
  );
}

function MarketingTestimonialPosition({
  company,
  position
}) {
  if (company === null && position === null) {
    return null;
  }

  if (company === null && position !== null) {
    return (
      <div className="leading-tight">
        {position}
      </div>
    );
  }

  if (company !== null && position === null) {
    return (
      <div className="leading-tight">
        {company}
      </div>
    );
  }

  return (
    <div className="leading-tight flex items-center space-y-px">
      <div className="text-green-100">
        {position}
      </div>
      &nbsp;en&nbsp;
      <div className="text-green-200">
        {company}
      </div>
    </div>
  );
}

function MarketingTestimonialCountry({
  country,
}) {
  if (country === null || country === undefined) {
    return null;
  }

  return (
    <div className="leading-tight text-gray-500 text-xs">
      {country}
    </div>
  );
}

function MarketingTestimonialDots({
  total,
  active,
  onDotClick,
}) {
  let items = [];

  for (let i = 0; i < total; i++) {
    items = [
      ...items,
      (
        <button
          key={`testimonial-dot-${i}`}
          className={clsx(
            'w-6 h-6 rounded-full ring ring-white focus:outline-none transition ease-in-out duration-300',
            active !== i && 'bg-gray-300 hover:bg-gray-500',
            active === i && 'bg-gray-900 pointer-events-none',
          )}
          onClick={() => onDotClick(i)}
          type="button"
        />
      )
    ];
  }

  return (
    <div className="flex items-center justify-center space-x-4">
      {items}
    </div>
  );
}

function MarketingTestimonial({
  title,
  featuredImage,
  info,
  isActive,
}) {
  if (isActive === false) {
    return null;
  }

  return (
    <div className="relative w-full">
      <figure className="shadow-xl rounded-xl flex-none w-full relative z-10">
        <blockquote className="rounded-t-xl bg-white px-6 py-8 md:p-10 text-lg md:text-xl leading-8 md:leading-8 font-medium text-gray-900">
          <svg width="45" height="36" className="mb-5 fill-current text-green-200">
            <path d="M13.415.001C6.07 5.185.887 13.681.887 23.041c0 7.632 4.608 12.096 9.936 12.096 5.04 0 8.784-4.032 8.784-8.784 0-4.752-3.312-8.208-7.632-8.208-.864 0-2.016.144-2.304.288.72-4.896 5.328-10.656 9.936-13.536L13.415.001zm24.768 0c-7.2 5.184-12.384 13.68-12.384 23.04 0 7.632 4.608 12.096 9.936 12.096 4.896 0 8.784-4.032 8.784-8.784 0-4.752-3.456-8.208-7.776-8.208-.864 0-1.872.144-2.16.288.72-4.896 5.184-10.656 9.792-13.536L38.183.001z" />
          </svg>
          <div dangerouslySetInnerHTML={{ __html: info.testimonial }} />
        </blockquote>
        <figcaption className="flex items-center space-x-4 p-6 md:px-10 md:py-6 rounded-b-xl leading-6 font-semibold text-white bg-green-600">
          <MarketingTestimonialImage title={title} {...featuredImage.node} />
          <div className="flex-auto space-y-px">
            <h5 className="text-lg font-semibold leading-normal">
              {title}
            </h5>
            <MarketingTestimonialPosition company={info.company} position={info.position} />
            <MarketingTestimonialCountry />
          </div>
        </figcaption>
      </figure>
    </div>
  );
}

export default function MarketingTestimonials({
  testimonials,
}) {
  const [active, setActive] = useState(0);

  return (
    <MarketingSection className="bg-gray-100" sectionTitle="Testimonios">
      <Container maxWidth="max-w-screen-lg">
        <div className="flex flex-col space-y-10">
          <div className="w-full">
            <ul className="flex flex-col">
              {testimonials.map(({ id, ...testimonial }, index) => (
                <li key={id} className="px-3 md:px-4 flex-none w-full max-w-screen-lg">
                  <MarketingTestimonial isActive={active === index} {...testimonial}/>
                </li>
              ))}
            </ul>
          </div>
          <MarketingTestimonialDots
            total={testimonials.length}
            active={active}
            onDotClick={setActive}
          />
        </div>
      </Container>
    </MarketingSection>
  );
}
